// client/src/components/PhotoUpload.js
// Gjenbrukbar komponent for bildeopplasting med klient-side resizing
import React, { useRef } from 'react';

const MAX_SIZE = 256; // px — maks bredde/høyde etter resize
const QUALITY  = 0.82; // JPEG-kvalitet

// Resize bilde til MAX_SIZExMAX_SIZE via Canvas
function resizeImage(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const ratio  = Math.min(MAX_SIZE / img.width, MAX_SIZE / img.height);
      const w      = Math.round(img.width  * ratio);
      const h      = Math.round(img.height * ratio);
      const canvas = document.createElement('canvas');
      canvas.width  = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      // Rund beskjæring
      ctx.beginPath();
      ctx.arc(w/2, h/2, Math.min(w,h)/2, 0, Math.PI*2);
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', QUALITY));
    };
    img.onerror = reject;
    img.src = url;
  });
}

export default function PhotoUpload({ photo, onPhoto, size = 80, label = 'Endre bilde' }) {
  const inputRef = useRef(null);

  const handleFile = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { alert('Bildet er for stort (maks 8 MB)'); return; }
    try {
      const resized = await resizeImage(file);
      onPhoto(resized);
    } catch { alert('Kunne ikke laste bildet'); }
    e.target.value = '';
  };

  return (
    <div style={pw.wrap}>
      <div style={{ position:'relative', width:size, height:size, flexShrink:0 }}>
        {/* Avatar-visning */}
        <div style={{ ...pw.avatar, width:size, height:size, fontSize:size*0.38 }}>
          {photo
            ? <img src={photo} alt="" style={pw.img} />
            : <span style={{ color:'#fff', fontWeight:800 }}>📷</span>
          }
        </div>
        {/* Kamera-overlay */}
        <button
          type="button"
          style={{ ...pw.cameraBtn, width:size*0.38, height:size*0.38, fontSize:size*0.2 }}
          onClick={() => inputRef.current?.click()}
          title={label}>
          📷
        </button>
      </div>
      <div style={pw.actions}>
        <button type="button" style={pw.changeBtn} onClick={() => inputRef.current?.click()}>
          {photo ? '🔄 Bytt bilde' : '📷 Last opp bilde'}
        </button>
        {photo && (
          <button type="button" style={pw.removeBtn} onClick={() => onPhoto(null)}>
            🗑 Fjern
          </button>
        )}
        <input ref={inputRef} type="file" accept="image/*"
          style={{ display:'none' }} onChange={handleFile} />
      </div>
    </div>
  );
}

// Hjelpefunksjon: vis avatar (foto eller initialer/emoji)
export function Avatar({ photo, name, emoji, color, size = 40, fontSize }) {
  const fs = fontSize || size * 0.38;
  const initials = name
    ? name.trim().split(/\s+/).map(w=>w[0]).join('').toUpperCase().slice(0,2)
    : '?';
  return (
    <div style={{
      width:size, height:size, borderRadius:'50%',
      background: color || '#7C6FF7',
      display:'flex', alignItems:'center', justifyContent:'center',
      overflow:'hidden', flexShrink:0, fontWeight:800,
    }}>
      {photo
        ? <img src={photo} alt={name||''} style={{ width:'100%', height:'100%', objectFit:'cover' }} />
        : <span style={{ fontSize:fs, color:'#fff', lineHeight:1 }}>{emoji || initials}</span>
      }
    </div>
  );
}

const pw = {
  wrap:      { display:'flex', alignItems:'center', gap:16 },
  avatar:    { borderRadius:'50%', background:'#EAE8F8', display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', position:'relative' },
  img:       { width:'100%', height:'100%', objectFit:'cover', borderRadius:'50%' },
  cameraBtn: { position:'absolute', bottom:0, right:0, borderRadius:'50%', background:'#1A1A35', border:'2px solid #fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 2px 6px rgba(0,0,0,.3)' },
  actions:   { display:'flex', flexDirection:'column', gap:6 },
  changeBtn: { padding:'7px 14px', borderRadius:9, border:'1px solid #EAE8F8', background:'#F7F5FF', color:'#7C6FF7', fontWeight:700, cursor:'pointer', fontSize:12, fontFamily:'inherit', textAlign:'left' },
  removeBtn: { padding:'7px 14px', borderRadius:9, border:'1px solid #FCA5A5', background:'#FFF5F5', color:'#C0312D', fontWeight:700, cursor:'pointer', fontSize:12, fontFamily:'inherit', textAlign:'left' },
};
